import { Injectable, signal, computed, inject, type OnDestroy } from '@angular/core';
import { SawDust, type LogEvent } from '@cudirect/shared-ui-library';
import type { Subscription } from 'rxjs';

/**
 * Generates a cryptographically strong unique ID using the Web Crypto API
 * @param length The length of the ID in bytes (default: 16)
 * @returns A hexadecimal string representation of the random bytes
 */
const uniqueId = (length = 16): string => {
  // Create a typed array of the specified length
  const buffer = new Uint8Array(length);

  // Fill the array with cryptographically random values
  window.crypto.getRandomValues(buffer);

  // Convert the array to a hexadecimal string
  return Array.from(buffer)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

export interface LogEntry extends LogEvent {
  id: string;
}

@Injectable({
  providedIn: 'root'
})
export class SawdustDevToolsService implements OnDestroy {
  private logs = signal<LogEntry[]>([]);
  private isOpen = signal<boolean>(false);
  private isMinimized = signal<boolean>(false);
  private activeFilter = signal<string>('all');
  private searchTerm = signal<string>('');
  private position = signal<{ x: number; y: number }>({ x: 20, y: 20 });
  private subscription: Subscription;

  readonly logger = inject(SawDust);

  readonly filteredLogs = computed(() => {
    const filter = this.activeFilter();
    const term = this.searchTerm().toLowerCase();

    return this.logs().filter((log) => {
      // Apply level filter
      if (filter !== 'all' && log.level !== filter) {
        return false;
      }

      // Apply search term
      if (term) {
        const messageMatch = log.message?.toLowerCase().includes(term);
        const contextMatch = JSON.stringify(log.context).toLowerCase().includes(term);
        const metadataMatch = JSON.stringify(log.metadata).toLowerCase().includes(term);

        return messageMatch || contextMatch || metadataMatch;
      }

      return true;
    });
  });

  constructor() {
    // Subscribe to log events from SawDust
    this.subscription = this.logger.logEventEmitter.subscribe((event) => {
      this.addLog({
        ...event,
        id: uniqueId()
      });
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  addLog(log: LogEntry): void {
    this.logs.update((logs) => [log, ...logs].slice(0, 1000)); // Keep last 1000 logs
  }

  clearLogs(): void {
    this.logs.set([]);
  }

  toggleOpen(): void {
    this.isOpen.update((open) => !open);
  }

  toggleMinimize(): void {
    this.isMinimized.update((min) => !min);
  }

  setFilter(filter: string): void {
    this.activeFilter.set(filter);
  }

  setSearchTerm(term: string): void {
    this.searchTerm.set(term);
  }

  setPosition(x: number, y: number): void {
    this.position.set({ x, y });
  }

  getIsOpen(): boolean {
    return this.isOpen();
  }

  getIsMinimized(): boolean {
    return this.isMinimized();
  }

  getPosition(): { x: number; y: number } {
    return this.position();
  }

  exportLogs(): void {
    const logs = this.logs();
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `sawdust-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
